// Revision tracking for one acceptance page (Stage 1 plan §D). Plain module with no Vue and no
// imports, so it runs under `node --test`.
//
// Every write sends the revision from the latest response, and writes run one after another, so
// a user never conflicts with their own earlier action: a 409 can only come from someone else.
//
//   const session = createDossierSession({ revision, onChange });
//   session.run((revision) => patchDossier(id, revision, commandId, set)) -> Promise<result>
//   session.reset(revision)  // after reloading the dossier
//   session.revision / session.busy / session.conflict

export function isConflictError(err) {
  const res = err && err.response;
  return Boolean(
    res &&
      res.status === 409 &&
      res.data &&
      res.data.code === "revision_conflict",
  );
}

export function createDossierSession({ revision = 0, onChange } = {}) {
  const state = { revision, conflict: null, pending: 0 };
  let queue = Promise.resolve();

  function snapshot() {
    return {
      revision: state.revision,
      busy: state.pending > 0,
      conflict: state.conflict,
    };
  }

  function notify() {
    if (onChange) onChange(snapshot());
  }

  // send(revision) must resolve with an object carrying the new `revision`.
  function run(send) {
    state.pending += 1;
    notify();
    const task = queue
      .then(async () => {
        if (state.conflict) {
          const error = new Error("conflict_pending");
          error.code = "conflict_pending";
          throw error;
        }
        try {
          const result = await send(state.revision);
          if (result && Number.isInteger(result.revision)) {
            state.revision = result.revision;
          }
          return result;
        } catch (err) {
          if (isConflictError(err)) {
            const details = err.response.data.details || {};
            state.conflict = {
              currentRevision: Number.isInteger(details.currentRevision)
                ? details.currentRevision
                : null,
            };
          }
          throw err;
        }
      })
      .finally(() => {
        state.pending -= 1;
        notify();
      });
    // The next action waits for this one whether it succeeds or fails.
    queue = task.catch(() => {});
    return task;
  }

  function reset(nextRevision) {
    state.revision = nextRevision;
    state.conflict = null;
    notify();
  }

  return {
    get revision() {
      return state.revision;
    },
    get busy() {
      return state.pending > 0;
    },
    get conflict() {
      return state.conflict;
    },
    run,
    reset,
  };
}
